import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Switch } from 'components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Progress } from 'components/ui/progress';
import {
  ArrowLeft, Activity, Clock, AlertCircle, AlertTriangle, CheckCircle,
  MapPin, User, RefreshCw, Bell, Settings, Eye, XCircle,
  Timer, Calendar, Shield, Zap
} from 'lucide-react';
import { toast } from 'sonner';

export default function ShiftMonitoring() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [activeTab, setActiveTab] = useState('live');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const [dashboardRes, alertsRes, settingsRes] = await Promise.all([
        api.get('/shift-monitoring/dashboard'),
        api.get('/shift-monitoring/alerts?acknowledged=false'),
        api.get('/shift-monitoring/settings')
      ]);

      setDashboard(dashboardRes.data.dashboard);
      setAlerts(alertsRes.data.alerts);
      setSettings(settingsRes.data.settings);
    } catch (error) {
      console.error('Failed to fetch monitoring data:', error);
      if (!showRefreshing) toast.error('Failed to load monitoring data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchData(true);
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const handleRunCheck = async () => {
    try {
      await api.post('/shift-monitoring/check-shifts', {});
      toast.success('Monitoring check initiated');
      setTimeout(() => fetchData(true), 2000);
    } catch (error) {
      toast.error('Failed to run monitoring check');
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await api.put(`/shift-monitoring/alerts/${alertId}/acknowledge`, {
        action_taken: "Reviewed"
      });
      toast.success('Alert acknowledged');
      setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleUpdateSettings = async (key, value) => {
    try {
      const newSettings = { ...settings, [key]: value };
      await api.put('/shift-monitoring/settings', newSettings);
      setSettings(newSettings);
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'critical': return 'bg-red-500';
      case 'warning': return 'bg-amber-500';
      case 'attention': return 'bg-yellow-500';
      default: return 'bg-green-500';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'critical': return <Badge className="bg-red-500">Critical</Badge>;
      case 'warning': return <Badge className="bg-amber-500">Warning</Badge>;
      case 'attention': return <Badge className="bg-yellow-500 text-yellow-900">Attention</Badge>;
      default: return <Badge className="bg-green-500">Normal</Badge>;
    }
  };

  const formatDuration = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Shift Monitoring</h1>
              <p className="text-xs sm:text-sm text-slate-500">Real-time shift tracking and alerts</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="h-9 w-9"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={handleRunCheck}
              size="sm"
              className="bg-teal-500 hover:bg-teal-600"
              data-testid="run-check-btn"
            >
              <Zap className="w-4 h-4" />
              <span className="hidden sm:inline">Run</span> Check
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Alert Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Active Shifts</p>
                  <p className="text-2xl sm:text-3xl font-bold text-slate-900">{dashboard?.active_count || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-teal-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Critical Alerts</p>
                  <p className="text-2xl sm:text-3xl font-bold text-red-500">{dashboard?.alerts_summary?.critical || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Warnings</p>
                  <p className="text-2xl sm:text-3xl font-bold text-amber-500">{dashboard?.alerts_summary?.warning || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Missed Clock-ins</p>
                  <p className="text-2xl sm:text-3xl font-bold text-orange-500">{dashboard?.missed_count || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-orange-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="live" data-testid="tab-live">
              Live Shifts
            </TabsTrigger>
            <TabsTrigger value="alerts" className="relative" data-testid="tab-alerts">
              Alerts
              {alerts.length > 0 && (
                <Badge className="ml-2 bg-red-500 text-xs">{alerts.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Live Shifts Tab */}
          <TabsContent value="live" className="mt-6">
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-teal-500" />
                    Active Shifts
                  </CardTitle>
                  <CardDescription>
                    Currently active caregiver shifts being monitored
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {dashboard?.active_shifts?.length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No active shifts at the moment</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {dashboard?.active_shifts?.map((shift) => (
                        <div
                          key={shift.shift_id}
                          className={`p-4 rounded-lg border ${
                            shift.status === 'critical' ? 'bg-red-50 border-red-200' :
                            shift.status === 'warning' ? 'bg-amber-50 border-amber-200' :
                            shift.status === 'attention' ? 'bg-yellow-50 border-yellow-200' :
                            'bg-slate-50 border-slate-200'
                          }`}
                          data-testid={`shift-${shift.shift_id}`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-4">
                              <div className={`w-3 h-3 rounded-full ${getStatusColor(shift.status)} animate-pulse`} />
                              <div>
                                <div className="flex items-center gap-2">
                                  <User className="w-4 h-4 text-slate-400" />
                                  <span className="font-medium text-slate-900">{shift.caregiver_name}</span>
                                  <span className="text-slate-400">→</span>
                                  <span className="text-slate-600">{shift.client_name}</span>
                                </div>
                                <div className="flex items-center gap-4 mt-1 text-sm">
                                  <span className="text-slate-500 flex items-center gap-1">
                                    <Timer className="w-3 h-3" />
                                    {formatDuration(shift.duration_hours)}
                                  </span>
                                  {shift.location_verified ? (
                                    <span className="text-green-600 flex items-center gap-1">
                                      <MapPin className="w-3 h-3" />
                                      GPS Verified
                                    </span>
                                  ) : (
                                    <span className="text-amber-600 flex items-center gap-1">
                                      <MapPin className="w-3 h-3" />
                                      No GPS
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="w-24 sm:w-32">
                                <Progress
                                  value={Math.min((shift.duration_hours / dashboard.thresholds.critical_hours) * 100, 100)}
                                  className="h-2"
                                />
                                <p className="text-xs text-slate-500 mt-1 text-center">
                                  {Math.round((shift.duration_hours / dashboard.thresholds.critical_hours) * 100)}% of limit
                                </p>
                              </div>
                              {getStatusBadge(shift.status)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Potentially Missed Clock-ins */}
              {dashboard?.potentially_missed?.length > 0 && (
                <Card className="border-orange-200">
                  <CardHeader>
                    <CardTitle className="text-orange-600 flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      Potentially Missed Clock-ins
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {dashboard.potentially_missed.map((shift) => (
                        <div
                          key={shift.shift_id}
                          className="p-3 rounded-lg bg-orange-50 border border-orange-200"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-slate-900 font-medium">{shift.caregiver_name}</p>
                              <p className="text-sm text-orange-600">
                                Scheduled for {shift.client_name} - {shift.minutes_overdue} minutes overdue
                              </p>
                            </div>
                            <Badge className="bg-orange-500">{shift.minutes_overdue}m late</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-500" />
                  Monitoring Alerts
                </CardTitle>
                <CardDescription>
                  Unacknowledged alerts requiring your attention
                </CardDescription>
              </CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-4" />
                    <p className="text-green-600 font-medium">All Clear!</p>
                    <p className="text-slate-500">No unacknowledged alerts</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alerts.map((alert) => (
                      <div
                        key={alert.alert_id}
                        className={`p-4 rounded-lg border ${
                          alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                          alert.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
                          'bg-blue-50 border-blue-200'
                        }`}
                        data-testid={`alert-${alert.alert_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            {alert.severity === 'critical' ? (
                              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                            ) : alert.severity === 'warning' ? (
                              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                            ) : (
                              <Bell className="w-5 h-5 text-blue-500 mt-0.5" />
                            )}
                            <div>
                              <p className={`font-medium ${
                                alert.severity === 'critical' ? 'text-red-700' :
                                alert.severity === 'warning' ? 'text-amber-700' :
                                'text-blue-700'
                              }`}>{alert.title}</p>
                              <p className="text-slate-600 text-sm mt-1">{alert.message}</p>
                              <p className="text-slate-400 text-xs mt-2">
                                {new Date(alert.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAcknowledgeAlert(alert.alert_id)}
                            data-testid={`ack-${alert.alert_id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                            Acknowledge
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-slate-500" />
                  Monitoring Settings
                </CardTitle>
                <CardDescription>
                  Configure alert thresholds and notification preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-medium text-slate-900 mb-4">Notification Channels</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Bell className="w-5 h-5 text-teal-500" />
                        <div>
                          <p className="text-slate-900">Push Notifications</p>
                          <p className="text-sm text-slate-500">Receive in-app alerts</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings?.push_alerts ?? true}
                        onCheckedChange={(v) => handleUpdateSettings('push_alerts', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Activity className="w-5 h-5 text-blue-500" />
                        <div>
                          <p className="text-slate-900">SMS Alerts</p>
                          <p className="text-sm text-slate-500">Receive critical alerts via SMS</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings?.sms_alerts ?? true}
                        onCheckedChange={(v) => handleUpdateSettings('sms_alerts', v)}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-slate-900 mb-4">Alert Types</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-slate-900">Extended Shift Duration</p>
                        <p className="text-sm text-slate-500">Alert when shifts exceed {settings?.extended_shift_alert_hours || 10}h</p>
                      </div>
                      <Switch
                        checked={settings?.alert_types?.extended_duration ?? true}
                        onCheckedChange={(v) => handleUpdateSettings('alert_types', {...settings?.alert_types, extended_duration: v})}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-slate-900">Critical Shift Duration</p>
                        <p className="text-sm text-slate-500">Urgent alert when shifts exceed {settings?.critical_shift_hours || 12}h</p>
                      </div>
                      <Switch
                        checked={settings?.alert_types?.critical_duration ?? true}
                        onCheckedChange={(v) => handleUpdateSettings('alert_types', {...settings?.alert_types, critical_duration: v})}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-slate-900">Missed Clock-in</p>
                        <p className="text-sm text-slate-500">Alert when scheduled shifts aren't started</p>
                      </div>
                      <Switch
                        checked={settings?.alert_types?.missed_clockin ?? true}
                        onCheckedChange={(v) => handleUpdateSettings('alert_types', {...settings?.alert_types, missed_clockin: v})}
                      />
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-teal-500" />
                    Current Thresholds
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Warning</p>
                      <p className="text-yellow-600 font-medium">{dashboard?.thresholds?.warning_hours || 8}h</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Alert</p>
                      <p className="text-amber-600 font-medium">{dashboard?.thresholds?.alert_hours || 10}h</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Critical</p>
                      <p className="text-red-600 font-medium">{dashboard?.thresholds?.critical_hours || 12}h</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Missed Grace</p>
                      <p className="text-orange-600 font-medium">{dashboard?.thresholds?.missed_grace_minutes || 30}m</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
